import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  TicketStatus,
  TicketPriority,
  TicketCategory,
  EscalationStatus,
  CallStatus,
  CallDirection,
} from '../types/support';
import { ContentStatus, ContentType, ContentSource } from '../types/content';

// ============================================================================
// Class name utility
// ============================================================================

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ============================================================================
// Date/Time formatting
// ============================================================================

export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Parse a date-only value (date of birth, etc.) without timezone conversion.
 * The API serializes these as UTC midnight ("1952-09-28T00:00:00.000Z"), so
 * `new Date(...)` renders the previous day for viewers west of UTC. Build the
 * date from its calendar parts at local midnight instead.
 */
export function parseDateOnly(dateString: string): Date {
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function formatCallDuration(seconds?: number): string {
  if (!seconds) return '--:--';
  return formatDuration(seconds);
}

// ============================================================================
// Status/Priority display helpers
// ============================================================================

export function getStatusLabel(status: TicketStatus): string {
  const labels: Record<TicketStatus, string> = {
    [TicketStatus.OPEN]: 'Open',
    [TicketStatus.IN_PROGRESS]: 'In Progress',
    [TicketStatus.WAITING_FOR_CUSTOMER]: 'Waiting',
    [TicketStatus.RESOLVED]: 'Resolved',
    [TicketStatus.CLOSED]: 'Closed',
  };
  return labels[status] || status;
}

export function getStatusBadgeClass(status: TicketStatus): string {
  const classes: Record<TicketStatus, string> = {
    [TicketStatus.OPEN]: 'badge-open',
    [TicketStatus.IN_PROGRESS]: 'badge-in-progress',
    [TicketStatus.WAITING_FOR_CUSTOMER]: 'badge-waiting',
    [TicketStatus.RESOLVED]: 'badge-resolved',
    [TicketStatus.CLOSED]: 'badge-closed',
  };
  return classes[status] || '';
}

export function getPriorityLabel(priority: TicketPriority): string {
  return priority.charAt(0) + priority.slice(1).toLowerCase();
}

export function getPriorityBadgeClass(priority: TicketPriority): string {
  const classes: Record<TicketPriority, string> = {
    [TicketPriority.LOW]: 'priority-low',
    [TicketPriority.MEDIUM]: 'priority-medium',
    [TicketPriority.HIGH]: 'priority-high',
    [TicketPriority.URGENT]: 'priority-urgent',
  };
  return classes[priority] || '';
}

export function getCategoryLabel(category: TicketCategory): string {
  const labels: Record<TicketCategory, string> = {
    [TicketCategory.BOOKING]: 'Booking',
    [TicketCategory.PAYMENT]: 'Payment',
    [TicketCategory.REFUND]: 'Refund',
    [TicketCategory.ACCOUNT]: 'Account',
    [TicketCategory.TECHNICAL]: 'Technical',
    [TicketCategory.GENERAL]: 'General',
  };
  return labels[category] || category;
}

export function getEscalationLabel(status: EscalationStatus): string {
  const labels: Record<EscalationStatus, string> = {
    [EscalationStatus.NONE]: 'None',
    [EscalationStatus.AGENT_REQUESTED_ADMIN_REVIEW]: 'Escalated',
    [EscalationStatus.ADMIN_ACKNOWLEDGED]: 'Acknowledged',
    [EscalationStatus.ADMIN_IN_PROGRESS]: 'Admin Reviewing',
    [EscalationStatus.ADMIN_RESOLVED]: 'Admin Resolved',
  };
  return labels[status] || status;
}

export function getCallStatusLabel(status: CallStatus): string {
  const labels: Record<CallStatus, string> = {
    [CallStatus.INITIATED]: 'Initiated',
    [CallStatus.RINGING]: 'Ringing',
    [CallStatus.IN_PROGRESS]: 'In Progress',
    [CallStatus.COMPLETED]: 'Completed',
    [CallStatus.BUSY]: 'Busy',
    [CallStatus.NO_ANSWER]: 'No Answer',
    [CallStatus.FAILED]: 'Failed',
    [CallStatus.CANCELED]: 'Canceled',
  };
  return labels[status] || status;
}

export function getCallDirectionLabel(direction: CallDirection): string {
  return direction === CallDirection.INBOUND ? 'Inbound' : 'Outbound';
}

// ============================================================================
// User display helpers
// ============================================================================

export function getUserDisplayName(user?: {
  firstName?: string;
  lastName?: string;
  email?: string;
  name?: string;
} | null, fallbackUserId?: string): string {
  if (!user) {
    // If we have a userId, show truncated version
    if (fallbackUserId) {
      return `User ${fallbackUserId.slice(0, 8)}...`;
    }
    return 'Customer';
  }
  // Some APIs return 'name' instead of firstName/lastName
  if (user.name) {
    return user.name;
  }
  if (user.firstName || user.lastName) {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  }
  return user.email || 'Customer';
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function formatPhoneNumber(phone?: string): string {
  if (!phone) return 'N/A';
  // Simple US format: +1 (XXX) XXX-XXXX
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
}

// ============================================================================
// Debounce utility
// ============================================================================

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// ============================================================================
// Toast notification helper (simple implementation)
// ============================================================================

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

let toastId = 0;
const toastListeners: ((toasts: Toast[]) => void)[] = [];
let currentToasts: Toast[] = [];

export function showToast(type: ToastType, message: string, duration = 5000) {
  const id = `toast-${++toastId}`;
  const toast: Toast = { id, type, message, duration };
  
  currentToasts = [...currentToasts, toast];
  toastListeners.forEach((listener) => listener(currentToasts));
  
  if (duration > 0) {
    setTimeout(() => dismissToast(id), duration);
  }
  
  return id;
}

export function dismissToast(id: string) {
  currentToasts = currentToasts.filter((t) => t.id !== id);
  toastListeners.forEach((listener) => listener(currentToasts));
}

export function subscribeToToasts(listener: (toasts: Toast[]) => void) {
  toastListeners.push(listener);
  listener(currentToasts);
  return () => {
    const index = toastListeners.indexOf(listener);
    if (index > -1) toastListeners.splice(index, 1);
  };
}

// ============================================================================
// Content Review display helpers
// ============================================================================

export function getContentStatusLabel(status: ContentStatus): string {
  const labels: Record<ContentStatus, string> = {
    [ContentStatus.UPLOADING]: 'Uploading',
    [ContentStatus.PROCESSING]: 'Processing',
    [ContentStatus.PENDING_REVIEW]: 'Pending Review',
    [ContentStatus.PENDING_IMPORT]: 'Pending Import',
    [ContentStatus.READY]: 'Ready',
    [ContentStatus.FAILED]: 'Failed',
    [ContentStatus.REJECTED]: 'Rejected',
  };
  return labels[status] || status;
}

export function getContentStatusBadgeVariant(
  status: ContentStatus
): 'default' | 'success' | 'warning' | 'destructive' | 'secondary' {
  const variants: Record<ContentStatus, 'default' | 'success' | 'warning' | 'destructive' | 'secondary'> = {
    [ContentStatus.UPLOADING]: 'secondary',
    [ContentStatus.PROCESSING]: 'default',
    [ContentStatus.PENDING_REVIEW]: 'warning',
    [ContentStatus.PENDING_IMPORT]: 'warning',
    [ContentStatus.READY]: 'success',
    [ContentStatus.FAILED]: 'destructive',
    [ContentStatus.REJECTED]: 'destructive',
  };
  return variants[status] || 'default';
}

export function getContentSourceLabel(source: ContentSource): string {
  const labels: Record<ContentSource, string> = {
    [ContentSource.UPLOAD]: 'Upload',
    [ContentSource.SORA]: 'AI (Sora)',
    [ContentSource.AI_GENERATED]: 'AI Generated',
    [ContentSource.TIKTOK_IMPORT]: 'TikTok',
    [ContentSource.INSTAGRAM_IMPORT]: 'Instagram',
  };
  return labels[source] || source;
}

export function getContentTypeLabel(type: ContentType): string {
  const labels: Record<ContentType, string> = {
    [ContentType.VIDEO]: 'Video',
    [ContentType.PHOTO]: 'Photo',
  };
  return labels[type] || type;
}

export function formatVideoDuration(seconds: number | null): string {
  if (!seconds) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// ============================================================================
// Number / Currency formatting
// ============================================================================

export function formatDollars(amount: number | null | undefined): string {
  if (amount == null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatDollarsCompact(amount: number | null | undefined): string {
  if (amount == null) return '$0';
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

export function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export function formatCentsDecimal(cents: number | null | undefined): string {
  if (cents == null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

export function formatNumber(n: number | null | undefined): string {
  if (n == null) return '0';
  return new Intl.NumberFormat('en-US').format(n);
}

export function formatCompactNumber(n: number | null | undefined): string {
  if (n == null) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

export function formatPercent(rate: number | null | undefined): string {
  if (rate == null) return '0.0%';
  return `${(rate * 100).toFixed(1)}%`;
}
